
import React, { useState } from "react";
import { Search, Users, Info, ChevronDown, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { formatRelativeTime } from "@/lib/utils";
import { User } from "./UserDetailView";
import UserDetailView from "./UserDetailView";
import { Skeleton } from "@/components/ui/skeleton";

interface UsersTabProps {
  users: User[];
  onContactUser: (user: User) => void;
  isLoading?: boolean;
}

// Source color mappings for badges
const sourceColors: Record<string, string> = {
  zillow: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  airbnb: "bg-red-100 text-red-800 hover:bg-red-200",
  facebook: "bg-indigo-100 text-indigo-800 hover:bg-indigo-200",
  google: "bg-green-100 text-green-800 hover:bg-green-200",
  direct: "bg-purple-100 text-purple-800 hover:bg-purple-200"
};

const UsersTab = ({ users, onContactUser, isLoading = false }: UsersTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [sortField, setSortField] = useState<"name" | "totalVideos" | "lastActive" | "signupDate">("lastActive");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  const filteredUsers = users.filter(user => {
    const matchesSearch = (user.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSource = sourceFilter === "all" || user.source === sourceFilter;
    return matchesSearch && matchesSource;
  }).sort((a, b) => {
    let aValue, bValue;
    if (sortField === "name") {
      aValue = a.name || "";
      bValue = b.name || "";
    } else if (sortField === "totalVideos") {
      aValue = a.totalVideos;
      bValue = b.totalVideos;
    } else if (sortField === "lastActive") {
      aValue = new Date(a.lastActive).getTime();
      bValue = new Date(b.lastActive).getTime();
    } else {
      aValue = new Date(a.signupDate).getTime();
      bValue = new Date(b.signupDate).getTime();
    }
    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field: "name" | "totalVideos" | "lastActive" | "signupDate") => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
  };

  const handleCloseUserDetail = () => {
    setSelectedUser(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="p-8 flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {selectedUser ? (
        <UserDetailView
          user={selectedUser}
          onClose={handleCloseUserDetail}
          onContact={onContactUser}
        />
      ) : (
        <>
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center">
                        User
                        {sortField === "name" && (
                          <ChevronDown className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "" : "rotate-180"}`} />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => handleSort("totalVideos")}
                    >
                      <div className="flex items-center">
                        Videos
                        {sortField === "totalVideos" && (
                          <ChevronDown className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "" : "rotate-180"}`} />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer hover:bg-muted/40 hidden md:table-cell"
                      onClick={() => handleSort("signupDate")}
                    >
                      <div className="flex items-center">
                        Signup Date
                        {sortField === "signupDate" && (
                          <ChevronDown className={`ml-1 h-4 w-4 ${sortDirection === "desc" ? "" : "rotate-180"}`} />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center h-32">
                        <div className="flex flex-col items-center justify-center text-muted-foreground">
                          <Users className="h-8 w-8 mb-2" />
                          <p>No users found matching your filters</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map(user => (
                      <TableRow key={user.id} className="cursor-pointer hover:bg-muted/50">
                        <TableCell className="font-medium" onClick={() => handleUserSelect(user)}>
                          <div className="flex flex-col">
                            <span>{user.name}</span>
                            <span className="text-sm text-muted-foreground">{user.email}</span>
                          </div>
                        </TableCell>
                        <TableCell onClick={() => handleUserSelect(user)}>{user.total_videos}</TableCell>
                        <TableCell className="hidden md:table-cell" onClick={() => handleUserSelect(user)}>
                          {formatRelativeTime(user.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => handleUserSelect(user)}>
                            <Info className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
};

export default UsersTab;
